import { useState } from 'react';
import {
    Box,
    Card,
    Grid,
    Stack,
    Typography,
    Button,
    IconButton,
    MenuItem,
    TextField,
    Chip,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Tooltip,
    Alert,
} from '@mui/material';
import { m, AnimatePresence } from 'framer-motion';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    PlayArrow as PlayIcon,
    DragIndicator as DragIcon,
    FilterList as FilterIcon,
    Settings as SettingsIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import Iconify from '../../../components/iconify';
import { reportingService } from '../../../api/reporting.service';
import { CustomQueryDto, QueryField, QueryFilter, QueryOperator } from '../../../@types/reporting';

// --- Constants & Metadata ---

const ENTITIES = [
    { id: 'appointment', label: 'Appointments', icon: 'eva:calendar-outline' },
    { id: 'patient', label: 'Patients', icon: 'eva:people-outline' },
    { id: 'product', label: 'Products', icon: 'eva:shopping-bag-outline' },
    { id: 'dispense', label: 'Dispenses', icon: 'eva:shopping-cart-outline' },
    { id: 'till', label: 'Financial (Till)', icon: 'eva:pricetags-outline' },
    { id: 'clinical', label: 'Clinical Records', icon: 'eva:file-text-outline' },
];

const FIELDS_METADATA: Record<string, string[]> = {
    appointment: ['id', 'title', 'startTime', 'endTime', 'appointmentStatusId', 'notes', 'clinicId', 'practitionerId', 'patientId'],
    patient: ['id', 'firstName', 'surName', 'lastName', 'email', 'phone', 'date_of_birth', 'gender', 'postCode'],
    product: ['id', 'name', 'type', 'category', 'currentStock', 'salePrice', 'purchasePrice'],
    dispense: ['id', 'totalPrice', 'status', 'createdDate', 'specType', 'type'],
    till: ['id', 'wallet', 'updatedDate'],
    clinical: ['id', 'type', 'createdDate'],
};

const OPERATORS = [
    { value: 'eq', label: 'Equals' },
    { value: 'neq', label: 'Not Equals' },
    { value: 'gt', label: 'Greater Than' },
    { value: 'gte', label: 'Greater Or Equal' },
    { value: 'lt', label: 'Less Than' },
    { value: 'lte', label: 'Less Or Equal' },
    { value: 'like', label: 'Contains' },
    { value: 'in', label: 'In List' },
    { value: 'between', label: 'Between' },
];

// ----------------------------------------------------------------------

export default function QueryBuilder() {
    const [baseEntity, setBaseEntity] = useState('appointment');
    const [selectedFields, setSelectedFields] = useState<QueryField[]>([]);
    const [filters, setFilters] = useState<QueryFilter[]>([]);
    const [results, setResults] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dragOver, setDragOver] = useState(false);

    const handleEntityChange = (entity: string) => {
        setBaseEntity(entity);
        setSelectedFields([]);
        setFilters([]);
        setResults(null);
    };

    // --- HTML5 Drag and Drop ---

    const onDragStart = (e: React.DragEvent, field: string) => {
        e.dataTransfer.setData('fieldId', field);
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const fieldId = e.dataTransfer.getData('fieldId');
        if (fieldId && !selectedFields.find((f) => f.field === fieldId)) {
            setSelectedFields([...selectedFields, { field: fieldId, alias: fieldId }]);
        }
    };

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(true);
    };

    const onDragLeave = () => {
        setDragOver(false);
    };

    // --- Logic ---

    const removeField = (index: number) => {
        const newFields = [...selectedFields];
        newFields.splice(index, 1);
        setSelectedFields(newFields);
    };

    const addFilter = () => {
        setFilters([...filters, { field: FIELDS_METADATA[baseEntity][0], operator: 'eq' as any, value: '' }]);
    };

    const updateFilter = (index: number, updates: Partial<QueryFilter>) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], ...updates };
        setFilters(newFilters);
    };

    const removeFilter = (index: number) => {
        const newFilters = [...filters];
        newFilters.splice(index, 1);
        setFilters(newFilters);
    };

    const executeQuery = async (async = false) => {
        if (selectedFields.length === 0) {
            setError('Please select at least one field');
            return;
        }

        setLoading(true);
        setError(null);

        const queryDto: CustomQueryDto = {
            baseEntity,
            fields: selectedFields,
            filters: filters.length > 0 ? filters : undefined,
        };

        try {
            if (async) {
                await reportingService.asyncCustomQuery(queryDto);
                setError('Report generation started in background. You will be notified when complete.');
            } else {
                const data = await reportingService.customQuery(queryDto);
                setResults(data);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to execute query');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
                {/* Sidebar - Available Fields */}
                <Grid item xs={12} md={3}>
                    <Card sx={{ p: 2, height: '100%', position: 'sticky', top: 24 }}>
                        <Typography variant="h6" gutterBottom>
                            Source Entity
                        </Typography>
                        <Stack spacing={1} sx={{ mb: 3 }}>
                            {ENTITIES.map((entity) => (
                                <Button
                                    key={entity.id}
                                    variant={baseEntity === entity.id ? 'contained' : 'outlined'}
                                    onClick={() => handleEntityChange(entity.id)}
                                    startIcon={<Iconify icon={entity.icon} />}
                                    fullWidth
                                    sx={{ justifyContent: 'flex-start' }}
                                >
                                    {entity.label}
                                </Button>
                            ))}
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DragIcon color="action" /> Available Fields
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                            Drag and drop fields into the workspace
                        </Typography>

                        <List>
                            {FIELDS_METADATA[baseEntity].map((field) => (
                                <ListItem
                                    key={field}
                                    draggable
                                    onDragStart={(e) => onDragStart(e, field)}
                                    sx={{
                                        mb: 1,
                                        bgcolor: 'background.neutral',
                                        borderRadius: 1,
                                        cursor: 'grab',
                                        '&:hover': { bgcolor: 'action.hover' },
                                        '&:active': { cursor: 'grabbing' },
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        <DragIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary={field} primaryTypographyProps={{ variant: 'body2' }} />
                                </ListItem>
                            ))}
                        </List>
                    </Card>
                </Grid>

                {/* Main Area - Builder */}
                <Grid item xs={12} md={9}>
                    <Stack spacing={3}>
                        {/* Field Selection (Drop Zone) */}
                        <Card sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SettingsIcon /> Selection Workspace
                            </Typography>
                            <Paper
                                variant="outlined"
                                onDrop={onDrop}
                                onDragOver={onDragOver}
                                onDragLeave={onDragLeave}
                                sx={{
                                    p: 3,
                                    minHeight: 120,
                                    bgcolor: dragOver ? 'action.hover' : 'background.neutral',
                                    borderStyle: 'dashed',
                                    borderColor: dragOver ? 'primary.main' : 'divider',
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <AnimatePresence mode="popLayout">
                                    {selectedFields.length === 0 ? (
                                        <m.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                        >
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                Drag fields here to include them in your report
                                            </Typography>
                                        </m.div>
                                    ) : (
                                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ width: '100%' }}>
                                            {selectedFields.map((field, index) => (
                                                <m.div
                                                    key={field.field}
                                                    layout
                                                    initial={{ scale: 0.8, opacity: 0 }}
                                                    animate={{ scale: 1, opacity: 1 }}
                                                    exit={{ scale: 0.8, opacity: 0 }}
                                                >
                                                    <Chip
                                                        label={field.field}
                                                        onDelete={() => removeField(index)}
                                                        color="primary"
                                                        variant="soft"
                                                        sx={{ mb: 1 }}
                                                    />
                                                </m.div>
                                            ))}
                                        </Stack>
                                    )}
                                </AnimatePresence>
                            </Paper>
                        </Card>

                        {/* Filter Builder */}
                        <Card sx={{ p: 2 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FilterIcon /> Filters
                                </Typography>
                                <Button startIcon={<AddIcon />} size="small" onClick={addFilter}>
                                    Add Filter
                                </Button>
                            </Stack>
                            {filters.length === 0 && (
                                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                                    No filters applied. Results will include all records.
                                </Typography>
                            )}
                            <Stack spacing={2}>
                                {filters.map((filter, index) => (
                                    <Stack key={index} direction="row" spacing={2} alignItems="center">
                                        <TextField
                                            select
                                            size="small"
                                            label="Field"
                                            value={filter.field}
                                            onChange={(e) => updateFilter(index, { field: e.target.value })}
                                            sx={{ minWidth: 150 }}
                                        >
                                            {FIELDS_METADATA[baseEntity].map((field) => (
                                                <MenuItem key={field} value={field}>
                                                    {field}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <TextField
                                            select
                                            size="small"
                                            label="Operator"
                                            value={filter.operator}
                                            onChange={(e) => updateFilter(index, { operator: e.target.value as any })}
                                            sx={{ minWidth: 120 }}
                                        >
                                            {OPERATORS.map((op) => (
                                                <MenuItem key={op.value} value={op.value}>
                                                    {op.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <TextField
                                            size="small"
                                            label="Value"
                                            value={filter.value}
                                            onChange={(e) => updateFilter(index, { value: e.target.value })}
                                            sx={{ flexGrow: 1 }}
                                        />
                                        <IconButton color="error" size="small" onClick={() => removeFilter(index)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Stack>
                                ))}
                            </Stack>
                        </Card>

                        {/* Actions */}
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            {error && (
                                <Alert severity={error.includes('started') ? 'success' : 'error'} sx={{ flexGrow: 1 }}>
                                    {error}
                                </Alert>
                            )}
                            <Button
                                variant="outlined"
                                color="inherit"
                                startIcon={<DownloadIcon />}
                                onClick={() => executeQuery(true)}
                                disabled={loading || selectedFields.length === 0}
                            >
                                Run Background (CSV)
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
                                onClick={() => executeQuery(false)}
                                disabled={loading || selectedFields.length === 0}
                            >
                                Preview Results
                            </Button>
                        </Stack>

                        {/* Results Preview */}
                        <AnimatePresence>
                            {results && (
                                <m.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 20 }}
                                >
                                    <Card sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            Results Preview ({results.data.length} records)
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        {results.headers.map((header: string) => (
                                                            <TableCell key={header} sx={{ bgcolor: 'background.neutral' }}>
                                                                {header}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {results.data.slice(0, 10).map((row: any, idx: number) => (
                                                        <TableRow key={idx}>
                                                            {results.headers.map((header: string) => (
                                                                <TableCell key={header}>{String(row[header] ?? '')}</TableCell>
                                                            ))}
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        {results.data.length > 10 && (
                                            <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                                                Showing first 10 records. Use background run for full dataset.
                                            </Typography>
                                        )}
                                    </Card>
                                </m.div>
                            )}
                        </AnimatePresence>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
}
